/**
 * This file contains a collection of functions for handling payments created online (back office, customer facing, etc)
 * that can be refunded in part or full as opposed to merely creating a new negative payment.
 */

/**
 * Check if there are any refundable payments for the supplied payment method in the supplied list of payments.
 *
 * @param {PaymentMethod} paymentMethod
 * @param {Payment[]} payments
 *
 * @returns {Payment[]}
 */
const getRefundablePaymentsForMethod = (paymentMethod, payments) => {
    return payments.filter((payment) => {
        return payment.paymentMethod.ID === paymentMethod.ID
            && (paymentMethod.supportsPartialRefunds || paymentMethod.supportsIntegratedRefundsInProPoint)
            && payment.outstandingRefundableAmount > 0;
    });
};

/**
 * @param {PaymentMethod} paymentMethod
 * @param {Payment[]} payments
 *
 * @returns {boolean}
 */
const refundablePaymentsExistForMethod = (paymentMethod, payments) => {
    return getRefundablePaymentsForMethod(paymentMethod, payments).length > 0;
};

/**
 * Is this payment currently refundable? The outstanding refundable balance must be > 0 and there cannot already be
 * a pending refund against this payment.
 *
 * @param {Payment} payment
 * @param {Payment[]} cartPayments
 *
 * @return {boolean}
 */
const isCurrentlyRefundable = (payment, cartPayments) => {
    // This should never be < 0 but let's not take that chance.
    if (!payment.outstandingRefundableAmount || payment.outstandingRefundableAmount <= 0) {
        return false;
    }

    return !cartPayments.some((cartPayment) => cartPayment.originalPaymentID === payment.ID);
};

/**
 * This function calculates the maximum amount that can be refunded for a given payment. This can't be more
 * than the remainingBalance of the order or be more than the original payment minus any refunds. We don't have to
 * worry about pending refunds here because the UI will prevent you from creating 2 partial refunds against the same
 * payment.
 *
 * @param {number} outstandingRefundableAmount
 * @param {number} remainingBalance
 *
 * @returns {number}
 */
const getMaxRefundableAmount = (outstandingRefundableAmount, remainingBalance) => {
    return Math.max(
        // The outstandingRefundableAmount is +ve so we need to reverse it.
        // Math.max is counter intuitive but we are looking for the smallest -ve number (closest to zero).
        outstandingRefundableAmount * -1,
        remainingBalance
    );
};

/**
 * This function filters the payments that could be used for a refund or have been used for refund. It excludes any
 * (partial) refund payments that have been made against this order. We can identify refund payments because they
 * will have an originalPaymentID that links them to the original order. Only include successful payments and show them
 * even if the outstandingRefundableAmount is 0 which is why we test paid not outstandingRefundableAmount.
 *
 * @param {Payment[]} payments
 * @param {string[]} refundablePaymentMethodRefs
 *
 * @returns {Payment[]}
 */
const getRefundablePayments = (payments, refundablePaymentMethodRefs) => {
    return payments.filter((payment) => {
        // Only show the original payment - not any refunds.
        return !payment.originalPaymentID
            && refundablePaymentMethodRefs.includes(payment.paymentMethod.ref)
            && payment.paid === true;
    });
};

/**
 * This function filters payments that refund another payment.
 *
 * @param {Payment[]} payments
 * @param {string[]} refundablePaymentMethodRefs
 *
 * @returns {Payment[]}
 */
const getRefundPayments = (payments, refundablePaymentMethodRefs) => {
    return payments.filter((payment) => {
        // Only show the refunds not any original payments.
        return payment.originalPaymentID
            && refundablePaymentMethodRefs.includes(payment.paymentMethod.ref)
            && payment.paid === true;
    });
};

module.exports = {
    refundablePaymentsExistForMethod,
    isCurrentlyRefundable,
    getMaxRefundableAmount,
    getRefundablePayments,
    getRefundablePaymentsForMethod,
    getRefundPayments,
};
